import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  COMMENT_SUGGESTIONS,
  IMAGE_SIZE_PRESETS,
  IMAGE_SIZE_ORDER,
  initialsOf,
  formatCommentTime,
  escapeHtml,
  highlightMentions,
} from "../../utils/commentUtils";

// Shared "Activity" comment thread (list + composer) used by both the Board's
// TaskDialog and the Ticket form. Supports @mentions, threaded replies (one
// level deep) and pasted-image attachments with a resizable preview.
//
// `fetchComments(entityId)` and `postComment(entityId, payload)` are injected
// so this component stays agnostic of whether it's backed by tasks or tickets.
function CommentThread({
  entityId,
  members = [],
  currentEmployeeId,
  currentEmployeeName = "",
  fetchComments,
  postComment,
}) {
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionedIds, setMentionedIds] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);
  const commentInputRef = useRef(null);

  useEffect(() => {
    if (!entityId) {
      setComments([]);
      return;
    }

    let cancelled = false;
    setCommentsLoading(true);

    fetchComments(entityId)
      .then((data) => { if (!cancelled) setComments(data); })
      .catch((error) => console.error("Load Comments Error:", error.response?.data || error))
      .finally(() => { if (!cancelled) setCommentsLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Jira-style "press M to comment" shortcut, ignored while typing elsewhere.
  useEffect(() => {
    if (!entityId) {
      return;
    }

    const handleKeyDown = (e) => {
      const targetTag = e.target.tagName;
      const isTyping = targetTag === "INPUT" || targetTag === "TEXTAREA" || e.target.isContentEditable;
      if (e.key.toLowerCase() === "m" && !isTyping) {
        e.preventDefault();
        commentInputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [entityId]);

  const topLevelComments = comments.filter((c) => !c.parent_comment_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_comment_id) {
      acc[c.parent_comment_id] = acc[c.parent_comment_id] || [];
      acc[c.parent_comment_id].push(c);
    }
    return acc;
  }, {});

  // Typing "@name" opens a suggestion list of team members to tag.
  const matchingMentionMembers =
    mentionQuery !== null
      ? members.filter((m) => m.employee_name.toLowerCase().includes(mentionQuery.toLowerCase()))
      : [];

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setCommentText(value);

    const textUpToCursor = value.slice(0, cursor);
    const atMatch = textUpToCursor.match(/@([\w.]*)$/);
    setMentionQuery(atMatch ? atMatch[1] : null);
  };

  const insertMention = (member) => {
    const cursor = commentInputRef.current?.selectionStart ?? commentText.length;
    const textUpToCursor = commentText.slice(0, cursor);
    const replaced = textUpToCursor.replace(/@([\w.]*)$/, `@${member.employee_name} `);
    const newValue = replaced + commentText.slice(cursor);

    setCommentText(newValue);
    setMentionedIds((prev) => (prev.includes(member.employee_id) ? prev : [...prev, member.employee_id]));
    setMentionQuery(null);
    commentInputRef.current?.focus();
  };

  // Pasted screenshots/images are staged as thumbnails (resizable before sending)
  // rather than inserted inline, so the plain-text comment box stays simple.
  const handleCommentPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type.startsWith("image/"));

    if (!imageItem) {
      return;
    }

    e.preventDefault();
    const file = imageItem.getAsFile();
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPendingImages((prev) => [
        ...prev,
        { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, dataUrl: reader.result, size: "medium" },
      ]);
    };
    reader.readAsDataURL(file);
  };

  const cycleImageSize = (imageId) => {
    setPendingImages((prev) =>
      prev.map((img) => {
        if (img.id !== imageId) return img;
        const nextIndex = (IMAGE_SIZE_ORDER.indexOf(img.size) + 1) % IMAGE_SIZE_ORDER.length;
        return { ...img, size: IMAGE_SIZE_ORDER[nextIndex] };
      })
    );
  };

  const removePendingImage = (imageId) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleAddComment = async () => {
    if ((!commentText.trim() && pendingImages.length === 0) || isPostingComment || !entityId) {
      return;
    }
    setIsPostingComment(true);
    try {
      const textHtml = commentText.trim() ? `<p>${escapeHtml(commentText.trim())}</p>` : "";
      const imagesHtml = pendingImages
        .map((img) => `<img src="${img.dataUrl}" style="width:${IMAGE_SIZE_PRESETS[img.size]}%;max-width:100%;border-radius:8px;margin-top:8px;display:block;" />`)
        .join("");

      const newComment = await postComment(entityId, {
        employee_id: currentEmployeeId,
        comment: textHtml + imagesHtml,
        mentioned_employee_ids: mentionedIds,
        parent_comment_id: replyingTo?.comment_id ?? null,
      });
      setComments((prev) => [...prev, newComment]);
      setCommentText("");
      setMentionedIds([]);
      setPendingImages([]);
      setReplyingTo(null);
    } catch (error) {
      console.error("Add Comment Error:", error.response?.data || error);
    } finally {
      setIsPostingComment(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
        Activity
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 320, overflowY: "auto", mb: 1.5, pr: 0.5 }}>
        {commentsLoading ? (
          [0, 1].map((placeholder) => (
            <Box key={placeholder} sx={{ display: "flex", gap: 1.2 }}>
              <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, borderRadius: 2, p: 1.2 }}>
                <Skeleton variant="text" width="30%" height={18} />
                <Skeleton variant="text" width="90%" height={16} />
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            </Box>
          ))
        ) : topLevelComments.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No comments yet</Typography>
        ) : (
          topLevelComments.map((comment) => (
            <Box key={comment.comment_id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", gap: 1.2 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "primary.main", flexShrink: 0 }}>
                  {initialsOf(comment.employee_name)}
                </Avatar>
                <Box sx={{ flex: 1, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.3}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "text.primary" }}>
                      {comment.employee_name || "Unknown"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      {formatCommentTime(comment.created_at)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      fontSize: 13,
                      color: "text.primary",
                      whiteSpace: "pre-wrap",
                      "& p": { m: 0 },
                      "& img": { mt: 1 },
                      "& .mention-highlight": { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 700, borderRadius: 0.6, px: 0.5, py: 0.1 },
                    }}
                    dangerouslySetInnerHTML={{ __html: highlightMentions(comment.comment, members) }}
                  />
                  <Typography
                    onClick={() => { setReplyingTo(comment); commentInputRef.current?.focus(); }}
                    sx={{ fontSize: 11.5, fontWeight: 700, color: "primary.main", cursor: "pointer", mt: 0.6, display: "inline-block" }}
                  >
                    Reply
                  </Typography>
                </Box>
              </Box>

              {(repliesByParent[comment.comment_id] || []).map((reply) => (
                <Box key={reply.comment_id} sx={{ display: "flex", gap: 1.2, pl: 4 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: "primary.main", flexShrink: 0 }}>
                    {initialsOf(reply.employee_name)}
                  </Avatar>
                  <Box sx={{ flex: 1, bgcolor: "background.default", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.3}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>
                        {reply.employee_name || "Unknown"}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
                        {formatCommentTime(reply.created_at)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        fontSize: 12.5,
                        color: "text.primary",
                        whiteSpace: "pre-wrap",
                        "& p": { m: 0 },
                        "& img": { mt: 1 },
                        "& .mention-highlight": { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 700, borderRadius: 0.6, px: 0.5, py: 0.1 },
                      }}
                      dangerouslySetInnerHTML={{ __html: highlightMentions(reply.comment, members) }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>

      <Box display="flex" gap={1.2} alignItems="flex-start">
        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "primary.main", flexShrink: 0, mt: 0.3 }}>
          {initialsOf(currentEmployeeName)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          {replyingTo && (
            <Chip
              label={`Replying to ${replyingTo.employee_name || "comment"}`}
              size="small"
              onDelete={() => setReplyingTo(null)}
              sx={{ mb: 1, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
            />
          )}

          <Box sx={{ position: "relative" }}>
            <TextField
              inputRef={commentInputRef}
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="Add a comment\u2026 (type @ to tag someone, paste an image)"
              value={commentText}
              disabled={isPostingComment}
              onChange={handleCommentChange}
              onPaste={handleCommentPaste}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
            />

            {mentionQuery !== null && matchingMentionMembers.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  mt: 0.5,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  boxShadow: 3,
                  maxHeight: 180,
                  overflowY: "auto",
                }}
              >
                {matchingMentionMembers.map((member) => (
                  <Box
                    key={member.employee_id}
                    onMouseDown={(e) => { e.preventDefault(); insertMention(member); }}
                    sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, py: 0.8, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <Avatar sx={{ width: 22, height: 22, fontSize: 10 }}>
                      {initialsOf(member.employee_name)}
                    </Avatar>
                    <Typography sx={{ fontSize: 13 }}>{member.employee_name}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {pendingImages.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {pendingImages.map((img) => (
                <Box
                  key={img.id}
                  sx={{
                    position: "relative",
                    width: 90,
                    height: 90,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    component="img"
                    src={img.dataUrl}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  <Chip
                    label={img.size}
                    size="small"
                    onClick={() => cycleImageSize(img.id)}
                    sx={{ position: "absolute", bottom: 3, left: 3, height: 18, fontSize: 9, cursor: "pointer", bgcolor: "rgba(0,0,0,0.6)", color: "#fff" }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removePendingImage(img.id)}
                    sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(0,0,0,0.6)", color: "#fff", width: 20, height: 20, "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.8} mt={1}>
            {COMMENT_SUGGESTIONS.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                size="small"
                onClick={() => setCommentText(suggestion.replace("...", " "))}
                sx={{ cursor: "pointer", bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
              />
            ))}

            <Box flex={1} />

            <Button
              variant="contained"
              size="small"
              onClick={handleAddComment}
              disabled={isPostingComment || (!commentText.trim() && pendingImages.length === 0)}
              startIcon={isPostingComment ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: "none" }}
            >
              {isPostingComment ? "Posting..." : "Comment"}
            </Button>
          </Box>

          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.8 }}>
            Pro tip: press <b>M</b> to comment
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default CommentThread;
